import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const faqs = [
  {
    question: 'What industries do you serve?',
    answer:
      'We work with home service companies (HVAC, plumbing, electrical, roofing, restoration), auto dealerships (service departments, sales inquiries, parts), and real estate teams (lead qualification, showing bookings, property inquiries). Any business that loses revenue when the phone goes unanswered is a fit.',
  },
  {
    question: 'What happens after the AI takes a call?',
    answer:
      'The AI creates a detailed summary of the call — who called, what they need, urgency level, and any relevant details — and sends it to your team via email, SMS, Slack, or your CRM. Full recordings and transcripts are available in your dashboard.',
  },
  {
    question: "What if the AI can't answer a question?",
    answer:
      "It captures the issue in full detail and notifies your human team immediately with a complete summary, transcript, and recommended next steps. No caller is left without a resolution path — they're told the right person will follow up shortly.",
  },
  {
    question: 'What does integration look like?',
    answer:
      'We connect to your existing CRM or communication tools (Salesforce, HubSpot, Zendesk, Freshdesk, Slack, email, and 400+ integrations). When a call comes in, the AI handles it and automatically sends a structured summary. We configure the integration during setup — no manual work on your end.',
  },
  {
    question: 'Can it handle questions specific to our business?',
    answer:
      "Yes. During setup, we train the AI on your knowledge base, service offerings, pricing, FAQ library, and common call flows. It learns YOUR business and your specific terminology — not generic responses.",
  },
  {
    question: 'What happens during the 14-day pilot?',
    answer:
      "We set up your AI agent, integrate it with your systems, and route real calls to it. You monitor every call via recordings and transcripts in a live dashboard. After 14 days, you decide if it's working. No payment until you say yes.",
  },
  {
    question: 'How is this different from a chatbot or IVR?',
    answer:
      "This is a voice conversation, not a phone tree. The AI talks to your caller naturally, asks follow-up questions, and captures structured information. It replaces voicemail — not your phone system. 67% of callers hang up on IVR systems. Our AI has a <5% abandonment rate.",
  },
  {
    question: 'How long does setup take?',
    answer:
      '48–72 hours from signed agreement to live. You approve the final agent configuration before any calls are routed. Most teams are fully integrated within 3 business days.',
  },
  {
    question: 'Do I need to change my phone system?',
    answer:
      "No. You keep your current number and phone system. We provide a dedicated AI number. You set up a simple forwarding rule: if your team doesn't answer after 15–25 seconds, the call forwards to AI. Your staff always answers first.\n\nIf you prefer AI as a primary number (for an after-hours line or overflow number), that works too. Either way, your existing phone system stays exactly as it is.",
  },
  {
    question: 'How many calls can it handle simultaneously?',
    answer:
      "Multiple calls at once — the exact number scales with your plan tier and grows as your volume does. During traffic spikes, burst capacity kicks in to handle overflow. No caller gets a busy signal.",
  },
  {
    question: 'What are the honest limitations?',
    answer:
      "The AI captures information and creates summaries — it does not transfer callers live to your team mid-call without their request. It cannot handle issues requiring screen sharing or remote access, calls requiring legal or compliance judgment, or deeply emotional situations. These are captured in full detail and flagged for immediate human follow-up.\n\nWhat it does handle conversationally: status inquiries, pricing questions, scheduling, service availability, troubleshooting walkthroughs, and FAQ resolution. We'd rather be honest about this than oversell.",
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="py-12 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggle(i)}
                className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                <SafeIcon
                  icon={openIndex === i ? FiChevronUp : FiChevronDown}
                  className="w-5 h-5 text-slate-500 flex-shrink-0"
                />
              </button>
              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-6 pb-6 pt-2 text-slate-600 leading-relaxed bg-slate-50 border-t border-slate-100 whitespace-pre-line">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;